import React, { FC, useEffect, useState } from 'react';
import {
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardMedia,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    Paper,
    Stack,
    TableCell,
    TableRow,
    TextField,
    Typography,
    styled,
    Checkbox,
    FormGroup,
    FormControlLabel,
    InputAdornment,
    Tooltip,
    Breadcrumbs,
    Link,
    Input,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    Box,
    CircularProgress,
    Toolbar,
    InputLabel,
    OutlinedInput,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../../atomic/atoms';
import { blue } from '@mui/material/colors';
import SCUITreeView from '../SCUITreeView/SCUITreeView';
import WebIcon from '@mui/icons-material/Web';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CreateNewFolderTwoToneIcon from '@mui/icons-material/CreateNewFolderTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteOutlineTwoToneIcon from '@mui/icons-material/DeleteOutlineTwoTone';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import HourglassEmptyTwoToneIcon from '@mui/icons-material/HourglassEmptyTwoTone';
import { sampleFolder, sampleTshirt } from '../../../../assets/images';
import {
    ImageButtonProps,
    ImageGalleryModalProps,
    ImageWithOverlayProps,
    PreviewWithOverlayProps,
} from '../../types/interfaces/CampaingTypes';
import { StepWebsiteAppearenceServiceComponent } from '../../Campaign.helper';

const imageButtonSx = {
    position: 'relative',
    height: 120,
    borderRadius: 15,
    margin: '8px 0',
    '&:hover, &.Mui-focusVisible': {
        zIndex: 1,
        '& .MuiImageBackdrop-root': {
            opacity: 0.1,
        },
        '& .MuiImageMarked-root': {
            opacity: 0,
        },
    },
    backgroundColor: '#ffffff',
} as const;

/** Stable text field so parent re-renders do not steal focus while typing */
const BadgeNameTextField: FC<{
    label: string;
    value: string;
    error?: boolean;
    helperText?: string;
    onChange?: (value: string) => void;
    onCommit?: (value: string) => void;
}> = ({ label, value, error, helperText, onChange, onCommit }) => {
    const [localValue, setLocalValue] = useState(value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    return (
        <TextField
            size="small"
            fullWidth
            label={label}
            value={localValue}
            onChange={(e) => {
                const nextValue = e.target.value;
                setLocalValue(nextValue);
                onChange?.(nextValue);
            }}
            onBlur={() => onCommit?.(localValue)}
            error={!!error}
            helperText={helperText}
            sx={{ mt: 1, mb: 1 }}
        />
    );
};

/** Stable campaign message field so controlled parent updates do not fight typing */
const CampaignMessageTextField: FC<{
    id: string;
    name: string;
    label: string;
    placeholder: string;
    value: string;
    error?: boolean;
    helperText?: string;
    onChange: (event: any) => void;
}> = ({ id, name, label, placeholder, value, error, helperText, onChange }) => {
    const [localValue, setLocalValue] = useState(value ?? '');

    useEffect(() => {
        setLocalValue(value ?? '');
    }, [value]);

    return (
        <TextField
            required
            id={id}
            name={name}
            label={label}
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => {
                setLocalValue(e.target.value);
                onChange(e);
            }}
            error={!!error}
            helperText={helperText}
        />
    );
};

export const SCUICampaignWebsiteAppearanceStep = ({ ...props }) => {
    const { t } = useTranslation();

    const helper = StepWebsiteAppearenceServiceComponent({ ...props });

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const Image = styled('span')(() => ({
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#626262',
    }));

    const ImageBackdrop = styled('span')(({ theme }) => ({
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        opacity: 0.2,
        transition: theme.transitions.create('opacity'),
        borderRadius: 15,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: '#fff',
    }));

    const ImageButton: FC<ImageButtonProps> = React.memo(({ culture, assetTarget }) => {
        return (
            <>
                <Button
                    focusRipple
                    component="label"
                    sx={imageButtonSx}
                    style={{
                        width: '141px',
                        height: '95px',
                        border: '1px dashed #c8c8c8',
                        borderRadius: '15px',
                    }}
                    onClick={() => {
                        helper.openModal();
                        helper.setSelectedBadgeButton({
                            culture: culture,
                            assetTarget: assetTarget,
                        });
                    }}
                >
                    <ImageBackdrop className="MuiImageBackdrop-root" />
                    <Image
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <FileUploadIcon style={{ width: 20, height: 20 }} />
                        <Typography
                            component="span"
                            color="inherit"
                            style={{ fontSize: '10px', padding: '8px', paddingBottom: '0' }}
                        >
                            {t('campaignManagement.save.step.websiteAppearance.badge.item.buttonPlaceHolder')}
                        </Typography>
                    </Image>
                </Button>
            </>
        );
    });

    const renderBadgeButtonsForLanguage = (item: any) => {
        const language = helper.languages?.find((x: any) => x.short_code == item.culture);

        return (
            <div key={item.culture}>
                <h4 style={{ marginBottom: '10px' }}> {language?.name ?? '-'}</h4>

                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={4}
                    useFlexGap
                    flexWrap="wrap"
                >
                    {item.badges &&
                        item.badges.map((badge: any) => (
                            <Card
                                key={`${item.culture}_${badge.assetTarget}`}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '141px',
                                }}
                            >
                                <Typography variant="caption" style={{ textAlign: 'center' }}>
                                    {badge.title}
                                </Typography>
                                {badge.source == '' ? (
                                    <ImageButton culture={item.culture} assetTarget={badge.assetTarget} />
                                ) : (
                                    <ImageWithOverlay
                                        helper={helper}
                                        badge={badge}
                                        editOnClick={() => {
                                            helper.openModal();
                                            helper.setSelectedBadgeButton({
                                                culture: item.culture,
                                                assetTarget: badge.assetTarget,
                                            });
                                        }}
                                        deleteOnClick={() => {
                                            helper.deleteCampaignBadge(item.culture, badge.assetTarget);
                                        }}
                                    />
                                )}
                                <BadgeNameTextField
                                    label={t('campaignManagement.save.step.websiteAppearance.badge.item.badgeName')}
                                    value={badge.badgeName ?? ''}
                                    error={helper.errors[`badgeName${item.culture}${badge.assetTarget}`]}
                                    helperText={helper.errors[`badgeName${item.culture}${badge.assetTarget}`]}
                                    onChange={(value) =>
                                        helper.handleChangeBadgeName(item.culture, badge.assetTarget, value)
                                    }
                                    onCommit={(value) =>
                                        helper.handleBlurBadgeName(item.culture, badge.assetTarget, value)
                                    }
                                />
                            </Card>
                        ))}
                </Stack>

                <Divider sx={{ mt: 2, mr: 2, mb: 2, ml: 0 }} style={{ width: '100%' }} />
            </div>
        );
    };

    const ImageGalleryModal: FC<ImageGalleryModalProps> = React.memo(({ isOpen, onClose, helper }) => {
        const BootstrapDialog = styled(Dialog)(({ theme }) => ({
            '& .MuiDialogContent-root': {
                padding: theme.spacing(0),
            },
            '& .MuiDialogActions-root': {
                padding: theme.spacing(0),
            },
            '& .MuiGrid-item': {
                paddingTop: `${theme.spacing(0)}!important`,
            },
            '& .add-image': {
                marginTop: '10px',
                marginBottom: '10px',
                width: '100%',
            },
            '& .left-panel': {
                backgroundColor: '#F6F6F6',
                height: '100%',
            },
            '& .right-panel': {},
            '& .main-folder': {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px',
                marginBottom: '8px',
            },
            '& .badge-list': {
                minHeight: '530px',
            },
            '& .selectable-tools': {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                margin: '8px 0',
            },
            '& .selectable-checkbox': {
                display: 'flex',
                justifyContent: 'space-between',
                width: '38%',
                flexDirection: 'row',
                alignItems: 'center',
            },
            '& .new-files': {
                marginTop: '16px',
                border: '1px dashed #bdbdbd',
                borderRadius: '5px',
            },
        }));

        const Item = styled(Paper)(({ theme }) => ({
            ...theme.typography.body2,
            padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        }));

        const isSelectedImage = (imageId: number) => {
            return helper.selectedImages.some((x: number) => x == imageId);
        };

        return (
            <BootstrapDialog
                onClose={onClose}
                aria-labelledby="image-galleries"
                open={isOpen}
                maxWidth={'lg'}
                fullWidth
            >
                <DialogTitle sx={{ m: 2, p: 2 }} id="customized-dialog-title" style={{ border: 0 }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                        {t('campaignManagement.save.step.websiteAppearance.modal.title')}
                    </Typography>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <Grid container>
                        <Grid
                            size={{
                                xs: 3,
                                sm: 3,
                                md: 3,
                                lg: 3,
                            }}
                        >
                            <CardContent className="left-panel">
                                <SCUIButton
                                    text={t('campaignManagement.save.step.websiteAppearance.modal.addImage')}
                                    type="primary"
                                    sizeType="fixed"
                                    size={'s'}
                                    startIcon={<AddIcon style={{ color: 'white' }} />}
                                    showStartIcon
                                    className="add-image"
                                    onClick={helper.handleAddImageClick}
                                />

                                <Input
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={helper.handleFileChange}
                                    inputProps={{ accept: 'image/*', multiple: true }}
                                    inputRef={helper.fileInputRef}
                                />

                                <Typography style={{ margin: '16px 0' }}>
                                    {t('campaignManagement.save.step.websiteAppearance.modal.folders')}
                                </Typography>

                                <SCUITreeView
                                    folders={helper.nestedFolders}
                                    onNodeSelect={helper.handleNodeSelect}
                                    defaultExpanded={helper.selectedTreeViewItem?.parents?.map((p: any) => p.id)}
                                    selected={helper.selectedTreeViewItem?.id}
                                />
                            </CardContent>
                        </Grid>
                        <Grid
                            size={{
                                xs: 9,
                                sm: 9,
                                md: 9,
                                lg: 9,
                            }}
                        >
                            <CardContent className="right-panel">
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel htmlFor="search-input">
                                        {t('campaignManagement.save.step.websiteAppearance.modal.searchImage')}
                                    </InputLabel>
                                    <OutlinedInput
                                        id="search-input"
                                        type={'text'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title={t(
                                                        'campaignManagement.save.step.websiteAppearance.modal.searchButton'
                                                    )}
                                                >
                                                    <IconButton
                                                        defaultValue={helper.searchTerm}
                                                        onClick={(event) => helper.handleSearch(event)}
                                                        edge="end"
                                                        style={{ margin: '0' }}
                                                        size="large"
                                                    >
                                                        <SearchIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        }
                                        label={t('campaignManagement.save.step.websiteAppearance.modal.searchImage')}
                                        defaultValue={helper.searchTerm}
                                        inputRef={helper.searchTermRef}
                                    />
                                </FormControl>

                                <div
                                    className="new-files"
                                    style={{
                                        display: helper.fileTable.length > 0 ? 'block' : 'none',
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        <Paper sx={{ width: '100%' }}>
                                            <Toolbar>
                                                <Typography
                                                    sx={{ flex: '1 1 100%' }}
                                                    variant="h6"
                                                    id="tableTitle"
                                                    component="div"
                                                >
                                                    {t(
                                                        'campaignManagement.save.step.websiteAppearance.modal.newImage.title'
                                                    )}
                                                </Typography>
                                                <Tooltip
                                                    title={t(
                                                        'campaignManagement.save.step.websiteAppearance.modal.newImage.action.uploadButton'
                                                    )}
                                                >
                                                    <IconButton
                                                        onClick={() => helper.handleUpload()}
                                                        size="large"
                                                        disabled={
                                                            helper.loading ||
                                                            !helper.selectedFiles.some(
                                                                (x: any) => x.status == 0 //todo: enum olacak
                                                            )
                                                        }
                                                    >
                                                        <FileUploadIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    title={t(
                                                        'campaignManagement.save.step.websiteAppearance.modal.newImage.action.closeButton'
                                                    )}
                                                >
                                                    <IconButton
                                                        onClick={helper.handleCloseNewFolder}
                                                        size="large"
                                                        disabled={helper.loading}
                                                    >
                                                        <CloseIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Toolbar>
                                            <TableContainer
                                                style={{
                                                    overflow: 'auto',
                                                    maxHeight: '243px',
                                                    display: 'flex',
                                                }}
                                            >
                                                <Table>
                                                    <TableHead>
                                                        <TableCell component="th">
                                                            {t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.column.id'
                                                            )}
                                                        </TableCell>
                                                        <TableCell component="th">
                                                            {t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.column.name'
                                                            )}
                                                        </TableCell>
                                                        <TableCell component="th">
                                                            {t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.column.type'
                                                            )}
                                                        </TableCell>
                                                        <TableCell component="th">
                                                            {t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.column.file'
                                                            )}
                                                        </TableCell>
                                                        <TableCell component="th">
                                                            {t(
                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.column.status'
                                                            )}
                                                        </TableCell>
                                                    </TableHead>
                                                    <TableBody>
                                                        {helper.fileTable.length > 0 &&
                                                            helper.fileTable.map((selectedFile: any, index: number) => {
                                                                return (
                                                                    <TableRow key={index}>
                                                                        <TableCell component="td">
                                                                            {selectedFile.id}
                                                                        </TableCell>
                                                                        <TableCell component="td">
                                                                            {selectedFile.name}
                                                                        </TableCell>
                                                                        <TableCell component="td">
                                                                            {selectedFile.type}
                                                                        </TableCell>
                                                                        <TableCell component="td">
                                                                            <img
                                                                                src={`data:${selectedFile.type};base64,${selectedFile.base64}`}
                                                                                style={{
                                                                                    height: '32px',
                                                                                    border: '1px solid #e0e0e0',
                                                                                }}
                                                                            ></img>
                                                                        </TableCell>
                                                                        <TableCell component="td">
                                                                            <Box sx={{ position: 'relative' }}>
                                                                                {
                                                                                    //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
                                                                                    !helper.loading &&
                                                                                        selectedFile.status == 0 && (
                                                                                            <Tooltip
                                                                                                title={t(
                                                                                                    'campaignManagement.save.step.websiteAppearance.modal.newImage.status.waiting'
                                                                                                )}
                                                                                            >
                                                                                                <HourglassEmptyTwoToneIcon
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            '24px',
                                                                                                        color: 'grey',
                                                                                                    }}
                                                                                                />
                                                                                            </Tooltip>
                                                                                        )
                                                                                }

                                                                                {
                                                                                    //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
                                                                                    selectedFile.status == 1 && (
                                                                                        <Tooltip
                                                                                            title={selectedFile.message}
                                                                                        >
                                                                                            <CancelTwoToneIcon
                                                                                                style={{
                                                                                                    fontSize: '24px',
                                                                                                    color: 'red',
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )
                                                                                }

                                                                                {
                                                                                    //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
                                                                                    selectedFile.status == 2 && (
                                                                                        <Tooltip
                                                                                            title={t(
                                                                                                'campaignManagement.save.step.websiteAppearance.modal.newImage.status.loaded'
                                                                                            )}
                                                                                        >
                                                                                            <CheckCircleTwoToneIcon
                                                                                                style={{
                                                                                                    fontSize: '24px',
                                                                                                    color: 'green',
                                                                                                }}
                                                                                            />
                                                                                        </Tooltip>
                                                                                    )
                                                                                }

                                                                                {
                                                                                    //todo : enum olacak 0: durumsuz, 1: pasif, 2: aktif
                                                                                    helper.loading &&
                                                                                        selectedFile.status == 0 && (
                                                                                            <CircularProgress
                                                                                                size={24}
                                                                                                sx={{
                                                                                                    color: blue[500],
                                                                                                    position:
                                                                                                        'absolute',
                                                                                                    top: -10,
                                                                                                    left: 10,
                                                                                                    zIndex: 1,
                                                                                                }}
                                                                                            />
                                                                                        )
                                                                                }
                                                                            </Box>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Paper>
                                    </Box>
                                </div>

                                <div className="main-folder">
                                    <Breadcrumbs aria-label="breadcrumb">
                                        <Link
                                            underline="hover"
                                            color="inherit"
                                            onClick={() => {
                                                helper.filterBadgeList();
                                                helper.setSelectedTreeViewItem({});

                                                if (helper.selectedTreeViewItem?.id != undefined) {
                                                    helper.handleCloseNewFolder();
                                                }
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {t('campaignManagement.save.step.websiteAppearance.modal.mainFolder')}
                                        </Link>
                                        {helper.selectedTreeViewItem &&
                                            helper.selectedTreeViewItem.parents?.map((p: any, index: number) => {
                                                return (
                                                    <Link
                                                        key={index}
                                                        underline="hover"
                                                        color="inherit"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(event) => {
                                                            helper.handleNodeSelect(event, p.id);
                                                            helper.filterBadgeList(p.id);
                                                        }}
                                                    >
                                                        {p.name}
                                                    </Link>
                                                );
                                            })}
                                        {helper.selectedTreeViewItem! && (
                                            <Typography color="text.primary">
                                                {helper.selectedTreeViewItem.name}
                                            </Typography>
                                        )}
                                    </Breadcrumbs>
                                    <IconButton
                                        aria-label="add-folder"
                                        size="large"
                                        onClick={helper.handleRequestNewFolder}
                                        disabled={helper.newFolder.isRequest}
                                    >
                                        <CreateNewFolderTwoToneIcon fontSize="inherit" />
                                    </IconButton>
                                </div>

                                <div className="badge-list">
                                    <div
                                        className="selectable-tools"
                                        style={{
                                            visibility:
                                                helper.searchResults.folders?.length > 0 ||
                                                helper.searchResults.assets?.length > 0
                                                    ? 'visible'
                                                    : 'hidden',
                                        }}
                                    >
                                        <div className="selectable-checkbox">
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={<Checkbox />}
                                                    label={
                                                        helper.selectAll
                                                            ? t(
                                                                  'campaignManagement.save.step.websiteAppearance.modal.removeAll'
                                                              )
                                                            : t(
                                                                  'campaignManagement.save.step.websiteAppearance.modal.selectAll'
                                                              )
                                                    }
                                                    // onClick={helper.toggleSelectAll}
                                                    checked={helper.selectAll}
                                                    disabled
                                                />
                                            </FormGroup>
                                        </div>
                                        <IconButton aria-label="delete" size="large" disabled={true}>
                                            <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                    </div>
                                    <Grid
                                        container
                                        spacing={2}
                                        justifyContent="flex-start"
                                        alignItems="center"
                                        flexWrap="wrap"
                                    >
                                        {helper.newFolder.isRequest && (
                                            <Grid
                                                size={{
                                                    xs: 12,
                                                    sm: 6,
                                                    md: 4,
                                                    lg: 3,
                                                }}
                                            >
                                                <Card style={{ margin: '8px 0', position: 'relative' }}>
                                                    <IconButton
                                                        onClick={() => {
                                                            helper.setNewFolder({
                                                                ...helper.newFolder,
                                                                name: '',
                                                                isRequest: false,
                                                            });
                                                        }}
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 8,
                                                            top: 8,
                                                            color: (theme) => theme.palette.grey[500],
                                                        }}
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                    <CardMedia
                                                        component="img"
                                                        height="170"
                                                        image={sampleFolder}
                                                        style={{
                                                            padding: '10px',
                                                            backgroundSize: 'auto',
                                                            width: '100%',
                                                            objectFit: 'scale-down',
                                                        }}
                                                        // onDoubleClick={(event: any) =>
                                                        //   helper.handleNodeSelect(event, item.id)
                                                        // }
                                                    />
                                                    <CardContent
                                                        style={{
                                                            padding: '8px',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        <FormControl fullWidth>
                                                            <TextField
                                                                id="folderName"
                                                                name="folderName"
                                                                label={t(
                                                                    'campaignManagement.save.step.websiteAppearance.modal.folderName'
                                                                )}
                                                                placeholder={t(
                                                                    'campaignManagement.save.step.websiteAppearance.modal.pressEnter'
                                                                )}
                                                                type="text"
                                                                size="small"
                                                                value={helper.newFolder.name}
                                                                onKeyDown={helper.saveNewFolder}
                                                                autoComplete="off"
                                                            />
                                                        </FormControl>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}

                                        {helper.searchResults &&
                                            helper.searchResults.folders?.map((item: any) => {
                                                return (
                                                    <Grid
                                                        key={item.id}
                                                        size={{
                                                            xs: 12,
                                                            sm: 6,
                                                            md: 4,
                                                            lg: 3,
                                                        }}
                                                    >
                                                        <Card
                                                            style={{
                                                                margin: '8px 0',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            {/* <Checkbox
                                  className={classes.checkbox}
                                  checked={helper.selectedImages.includes(
                                    item.id
                                  )}
                                  onChange={() =>
                                    helper.toggleImageSelection(item.id)
                                  }
                                /> */}
                                                            <CardMedia
                                                                component="img"
                                                                height="170"
                                                                image={sampleFolder}
                                                                style={{
                                                                    padding: '10px',
                                                                    backgroundSize: 'auto',
                                                                    width: '100%',
                                                                    objectFit: 'scale-down',
                                                                }}
                                                                onDoubleClick={(event: any) =>
                                                                    helper.handleNodeSelect(event, item.id)
                                                                }
                                                            />
                                                            <CardContent
                                                                style={{
                                                                    padding: '8px',
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                <Tooltip title={item.name}>
                                                                    <Typography
                                                                        gutterBottom
                                                                        variant="h5"
                                                                        component="div"
                                                                    >
                                                                        {item.name?.length <= 20
                                                                            ? item.name
                                                                            : `${item.name.substring(0, 20)}...`}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}

                                        {helper.searchResults &&
                                            helper.searchResults.assets?.map((item: any) => {
                                                return (
                                                    <Grid
                                                        key={item.id}
                                                        size={{
                                                            xs: 12,
                                                            sm: 6,
                                                            md: 4,
                                                            lg: 3,
                                                        }}
                                                    >
                                                        <Card
                                                            style={{
                                                                margin: '8px 0',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            <Checkbox
                                                                sx={{ position: 'absolute', top: '8px', right: '8px' }}
                                                                checked={helper.selectedImages.includes(item.id)}
                                                                onChange={() => helper.toggleImageSelection(item.id)}
                                                            />
                                                            <CardMedia
                                                                component="img"
                                                                height="170"
                                                                image={item.assetUrl}
                                                                style={{
                                                                    border: isSelectedImage(item.id)
                                                                        ? '2px solid #0d6efd'
                                                                        : '1px solid #c8c8c8',
                                                                    borderBottom: isSelectedImage(item.id)
                                                                        ? ''
                                                                        : '1px solid #c8c8c8',
                                                                    borderRadius: '5px 5px 0 0',
                                                                    padding: '10px',
                                                                    backgroundSize: 'auto',
                                                                    // maxWidth:"200px"
                                                                    width: '100%',
                                                                    objectFit: 'scale-down',
                                                                    backgroundColor: '#f6f6f6',
                                                                }}
                                                                onDoubleClick={() => {
                                                                    helper.doubleClickImageSelection(item.id);
                                                                }}
                                                            />
                                                            <CardContent
                                                                style={{
                                                                    borderBottom: isSelectedImage(item.id)
                                                                        ? '2px solid #0d6efd'
                                                                        : '1px solid #c8c8c8',
                                                                    borderLeft: isSelectedImage(item.id)
                                                                        ? '2px solid #0d6efd'
                                                                        : '1px solid #c8c8c8',
                                                                    borderRight: isSelectedImage(item.id)
                                                                        ? '2px solid #0d6efd'
                                                                        : '1px solid #c8c8c8',
                                                                    borderRadius: '0 0 5px 5px',
                                                                    padding: '8px',
                                                                }}
                                                                onDoubleClick={() => {
                                                                    helper.doubleClickImageSelection(item.id);
                                                                }}
                                                            >
                                                                <Tooltip title={item.fileName}>
                                                                    <Typography
                                                                        gutterBottom
                                                                        variant="h5"
                                                                        component="div"
                                                                    >
                                                                        {item.fileName.length <= 20
                                                                            ? item.fileName
                                                                            : `${item.fileName.substring(0, 20)}...`}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                );
                                            })}
                                    </Grid>
                                </div>
                            </CardContent>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Stack direction="row">
                        <Item style={{ padding: '16px 10px 16px 16px' }}>
                            <SCUIButton
                                text={t('campaignManagement.save.step.websiteAppearance.modal.action.cancelButton')}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                onClick={onClose}
                            />
                        </Item>
                        <Item style={{ padding: '16px 16px 16px 10px' }}>
                            <SCUIButton
                                autoFocusButton={true}
                                text={t('campaignManagement.save.step.websiteAppearance.modal.action.saveButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                onClick={() => helper.saveModal()}
                            />
                        </Item>
                    </Stack>
                </DialogActions>
            </BootstrapDialog>
        );
    });

    const ImageWithOverlay: FC<ImageWithOverlayProps> = React.memo(({ helper, badge, editOnClick, deleteOnClick }) => {
        let checkVisible = helper.imageOverlay.id == badge.id && helper.imageOverlay.visible;

        return (
            <div
                style={{
                    position: 'relative',
                    margin: '8px 0',
                    display: 'inline-block',
                    overflow: 'hidden', // Resmin dışına taşan overlay'ı kes
                }}
            >
                <img
                    src={badge.source}
                    style={{
                        width: '141px',
                        height: '95px',
                        objectFit: 'scale-down',
                    }}
                    onMouseOver={(event) => helper.handleImageMouseOver(event, badge.id)}
                />

                {checkVisible && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '5px',
                            zIndex: '999',
                        }}
                        onMouseLeave={(event) => helper.handleImageMouseLeave(event, badge.id)}
                    >
                        <div style={{ textAlign: 'center', color: '#fff' }}>
                            <IconButton
                                aria-label="alarm"
                                size="large"
                                style={{ backgroundColor: 'white', margin: 3 }}
                                onClick={editOnClick}
                            >
                                <EditTwoToneIcon style={{ fontSize: '16px', color: 'black' }} />
                            </IconButton>
                            <IconButton
                                aria-label="delete"
                                size="large"
                                style={{ backgroundColor: 'white', margin: 3 }}
                                onClick={deleteOnClick}
                            >
                                <DeleteOutlineTwoToneIcon style={{ fontSize: '16px', color: 'red' }} />
                            </IconButton>
                        </div>
                    </div>
                )}
            </div>
        );
    });

    const PreviewWithOverlay: FC<PreviewWithOverlayProps> = React.memo(({ imageSource, overlayVisible }) => {
        return (
            <div
                style={{
                    position: 'relative',
                    overflow: 'hidden', // Resmin dışına taşan overlay'ı kes
                }}
            >
                <CardMedia sx={{ height: 469 }} image={sampleTshirt} style={{ backgroundSize: 'auto' }} title={''} />

                {overlayVisible && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 24,
                            left: 8,
                        }}
                    >
                        <img src={imageSource} style={{ width: '56px' }} />
                    </div>
                )}
            </div>
        );
    });
    return (
        <Card className="step-form-card">
            <form
                id="StepWebSiteAppearance"
                name="StepWebSiteAppearance"
                noValidate={false}
                // onSubmit={helper.stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <WebIcon sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.websiteAppearance.title')}
                    subheader={t('campaignManagement.save.step.websiteAppearance.description')}
                />

                <CardContent className="step-form-card-content">
                    <Grid container>
                        <Grid
                            size={{
                                xs: 5,
                                sm: 5,
                                md: 5,
                                lg: 5,
                            }}
                        >
                            <Card>
                                <CardContent style={{ padding: '0' }}>
                                    <h3 style={{ marginBottom: '10px' }}>
                                        {t('campaignManagement.save.step.websiteAppearance.form.campaignMessage.title')}
                                    </h3>
                                    {helper.languages.length > 0 &&
                                        helper.languages.map((language: any) => {
                                            let id = `${language.id}`;
                                            let name = `campaignMessage${language.name}`;
                                            let label = t(
                                                'campaignManagement.save.step.websiteAppearance.form.campaignMessage.title'
                                            );
                                            let placeholder = t(
                                                'campaignManagement.save.step.websiteAppearance.form.campaignMessage.placeHolder'
                                            );

                                            let messageItem = helper.campaignMessages?.find(
                                                (c: any) => c.languageId == language.id
                                            );
                                            let value = messageItem?.keywordValue ?? '';

                                            return (
                                                <div key={language.id}>
                                                    <Typography component="h2">{language.name}</Typography>
                                                    <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                                                        <CampaignMessageTextField
                                                            id={id}
                                                            name={name}
                                                            label={label}
                                                            placeholder={placeholder}
                                                            value={value}
                                                            onChange={helper.handleChangeCampaignMessage}
                                                            error={helper.errors[name]}
                                                            helperText={helper.errors[name]}
                                                        />
                                                    </FormControl>
                                                </div>
                                            );
                                        })}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Divider style={{ width: '100%', margin: '16px 0' }} />

                    <Grid container>
                        <Grid
                            size={{
                                xs: 9,
                                sm: 9,
                                md: 9,
                                lg: 9,
                            }}
                        >
                            <Card>
                                <CardContent style={{ padding: '0' }}>
                                    <h3>{t('campaignManagement.save.step.websiteAppearance.badge.title')}</h3>
                                    <Typography component="h3" style={{ margin: '16px 0' }}>
                                        {t('campaignManagement.save.step.websiteAppearance.badge.description')}
                                    </Typography>

                                    <Divider sx={{ mt: 2, mr: 2, mb: 2, ml: 0 }} style={{ width: '100%' }} />

                                    {helper.badgeButtons &&
                                        helper.badgeButtons.map((item: any) => renderBadgeButtonsForLanguage(item))}
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid
                            size={{
                                xs: 3,
                                sm: 3,
                                md: 3,
                                lg: 3,
                            }}
                        >
                            <div className="preview-title">
                                <Typography gutterBottom variant="h5" component="div">
                                    {t('campaignManagement.save.step.websiteAppearance.badge.review')}
                                </Typography>
                            </div>
                            <Card sx={{ width: '100%' }} style={{ border: '1px solid #C8C8C8' }}>
                                <PreviewWithOverlay
                                    overlayVisible={helper.previewOverlay.visible}
                                    imageSource={helper.previewOverlay.imageSource}
                                />
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>

                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.backButton')}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                buttonType="button"
                                onClick={helper.stepperOnClickReset}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveButton')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={() => helper.saveWebsiteAppearanceCampaign()}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveAndContinueButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={helper.saveForm}
                            />
                        </Item>
                    </Stack>
                </CardActions>

                {helper.isModalOpen && (
                    <ImageGalleryModal
                        {...props}
                        isOpen={helper.isModalOpen}
                        onClose={helper.closeModal}
                        helper={helper}
                    />
                )}
            </form>
        </Card>
    );
};
